// CTV-445 / CTV-560: daypart buckets — group the 24 hourly rows into six equal
// 4-hour windows for the report's daypart chart + insights band. The API still
// returns granular hours; we bucket client-side so the data stays flexible.
//
// CTV-560 replaced the eight uneven legacy broadcast windows (Early Fringe /
// Primetime / Late News / …) with six equal 4-hour blocks aligned to midnight,
// so bar widths in the chart no longer mislead volume comparisons. Windows are
// whole-hour, half-open [startHour, endHour); no wraparound (the legacy
// `[0, 2)` Late Night case is gone).

export interface Daypart {
  label: string; // display name, e.g. "Evening"
  name: string; // lowercase prose form, e.g. "evening"
  startHour: number; // inclusive
  endHour: number; // exclusive; window is [startHour, endHour)
}

export const DAYPARTS: Daypart[] = [
  { label: 'Overnight', name: 'overnight', startHour: 0, endHour: 4 },
  { label: 'Early Morning', name: 'early morning', startHour: 4, endHour: 8 },
  { label: 'Daytime', name: 'daytime', startHour: 8, endHour: 12 },
  { label: 'Afternoon', name: 'afternoon', startHour: 12, endHour: 16 },
  { label: 'Evening', name: 'evening', startHour: 16, endHour: 20 },
  { label: 'Late Night', name: 'late night', startHour: 20, endHour: 24 },
];

export interface DaypartBucket {
  label: string;
  name: string;
  hours: string; // human time range, e.g. "8–11pm" (from start/end hours)
  impressions: number;
  completes: number;
}

// Format a window's [startHour, endHour) as a compact time range, e.g.
// "5–9am", "9am–4pm", "11pm–12am". The am/pm is collapsed to one side when both
// boundaries share it ("8–11pm") and shown on both when they differ ("9am–4pm").
function boundary(h: number): { h12: number; period: 'am' | 'pm' } {
  const hh = (((h % 24) + 24) % 24);
  const period = hh < 12 ? 'am' : 'pm';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return { h12, period };
}

export function formatHourRange(startHour: number, endHour: number): string {
  const s = boundary(startHour);
  const e = boundary(endHour);
  return s.period === e.period
    ? `${s.h12}–${e.h12}${e.period}`
    : `${s.h12}${s.period}–${e.h12}${e.period}`;
}

// CTV-445: the API returns the 24 hours in UTC plus a representative
// `offsetMinutes` for the campaign's market zone. Shift the aggregated hours to
// local viewer time before bucketing so the daypart reads as "primetime" rather
// than "1am UTC". US zones are whole-hour offsets, so we rotate the 24-slot
// array by whole hours; a null/0 offset (unknown zone) leaves it as UTC.
export function shiftHoursToLocal<T extends { hour: number; impressions: number; completes?: number }>(
  byHour: T[],
  offsetMinutes: number | null | undefined,
): Array<{ hour: number; impressions: number; completes: number }> {
  const out = Array.from({ length: 24 }, (_, hour) => ({ hour, impressions: 0, completes: 0 }));
  const shift = Math.round((offsetMinutes ?? 0) / 60);
  for (const h of byHour) {
    const local = (((h.hour + shift) % 24) + 24) % 24;
    out[local].impressions += h.impressions ?? 0;
    out[local].completes += h.completes ?? 0;
  }
  return out;
}

export function bucketByDaypart(
  byHour: Array<{ hour: number; impressions: number; completes?: number }>,
): DaypartBucket[] {
  return DAYPARTS.map((dp) => {
    let impressions = 0;
    let completes = 0;
    for (const h of byHour) {
      if (h.hour >= dp.startHour && h.hour < dp.endHour) {
        impressions += h.impressions ?? 0;
        completes += h.completes ?? 0;
      }
    }
    return { label: dp.label, name: dp.name, hours: formatHourRange(dp.startHour, dp.endHour), impressions, completes };
  });
}
